import {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Nullable} from "primereact/ts-helpers";
import {Booking, NewBooking} from "../data/Booking";
import {AvailableSlot} from "../data/Slot";
import CalendarComponent from "./CalendarComponent";
import ContactComponent from "./ContactComponent";
import SlotsComponent from "./SlotsComponent";
import {Toast} from "primereact/toast";
import {ProgressSpinner} from "primereact/progressspinner";
import logo from "./../buchmi.svg";

function CustomerBooking() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [availableSlots, setAvailableSlots] = useState([] as AvailableSlot[]);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const toast = useRef<Toast>(null);

    async function fetchSlots() {
        setIsLoading(true);

        const utcNow = new Date().toISOString();
        console.log(process.env.REACT_APP_BACKEND_URL);
        const response = await fetch(
            `/api/slot/entries/available?startDate=${utcNow}`,
            {
                credentials: "include",
            },
        );
        const resData = await response.json();
        const slots = resData.map((element: any) => {
            const slot = Object.assign(new AvailableSlot(), element);

            slot.startDateTime = new Date(element.startDateTime);
            slot.endDateTime = new Date(element.endDateTime);

            return slot;
        });

        setAvailableSlots(slots);
        setIsLoading(false);
    }

    useEffect(() => {
        fetchSlots();
    }, []);

    const calendarRef = useRef<HTMLDivElement>(null);
    const slotRef = useRef<HTMLDivElement>(null);
    const contactRef = useRef<HTMLDivElement>(null);

    const scrollToSlot = () => {
        requestAnimationFrame(() => {
            slotRef.current?.scrollIntoView({behavior: "smooth"});
        });
    };
    const scrollToContact = () => {
        requestAnimationFrame(() => {
            contactRef.current?.scrollIntoView({behavior: "smooth"});
        });
    };

    const navigate = useNavigate();

    async function onSubmitClicked(newBooking: NewBooking) {
        setIsSubmitting(true);

        try {
            const response = await fetch(
                `/api/booking/entries`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        slotId: newBooking.slotId,
                        amount: newBooking.amount,
                        name: newBooking.name,
                        email: newBooking.email,
                        phone: newBooking.phone,
                        comment: newBooking.comment,
                    }),
                    credentials: "include",
                },
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const resData = await response.json();
            const booking = Object.assign(new Booking(), resData);

            navigate(`/booking/${booking.id}`);
        } catch (error) {
            toast.current?.show({
                severity: "error",
                summary: "Fehler",
                detail: "Buchung konnte nicht übermittelt werden.",
                life: 3000,
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    function onDateSelected(date: Nullable<Date>): void {
        setSelectedSlot(null);
        if (date === undefined || date === null) {
            setSelectedDate(null);
            return;
        }
        setSelectedDate(date);
        scrollToSlot();
    }

    function onSlotSelected(slot: Nullable<AvailableSlot>): void {
        if (slot == null) {
            setSelectedSlot(null);
            return;
        }

        setSelectedSlot(slot);
        scrollToContact();
    }

    return (
        <div
            className="App"
            style={{
                maxWidth: "800px",
                height: "100dvh",
                margin: "0 auto",
                padding: "1rem", // Adds spacing around the calendar
                boxSizing: "border-box",
            }}
        >
            <div className="my-5 flex flex-column justify-content-center flex-wrap">
                <div>
                    <img src={logo} alt="Buchmi Logo" className="w-12rem "/>
                </div>
                <div className="text-6xl font-bold mt-2">Termine für's Moimio</div>
                <p className="text-color-secondary">
                    Kein passender Termin dabei? Schreib' mir persönlich über{" "}
                    <a href="https://wa.me/4994423115">WhatsApp</a> oder{" "}
                    <a href="mailto:info@moimio.de">E-Mail</a>.
                </p>
            </div>
            {isLoading && <ProgressSpinner/>}
            {!isLoading && (
                <div>
                    <Toast ref={toast}/>
                    {
                        <div ref={calendarRef} className="flex flex-column gap-5">
                            <CalendarComponent
                                selectedDate={selectedDate}
                                availableSlots={availableSlots}
                                onDateSelected={onDateSelected}
                            />
                            {selectedDate != null && (
                                <div ref={slotRef}>
                                    <SlotsComponent
                                        selectedSlot={selectedSlot}
                                        selectedDate={selectedDate}
                                        slots={availableSlots.filter(
                                            (slot) =>
                                            {
                                                return selectedDate.getFullYear() === slot.startDateTime.getFullYear() &&
                                                    selectedDate.getMonth() === slot.startDateTime.getMonth() &&
                                                    selectedDate.getDate() === slot.startDateTime.getDate();
                                            },
                                        ).sort((s1, s2) => s1.startDateTime.getTime() - s2.startDateTime.getTime())}
                                        onSlotSelected={onSlotSelected}
                                    />
                                </div>
                            )}
                            {selectedSlot != null && (
                                <div ref={contactRef}>
                                    <ContactComponent
                                        selectedSlot={selectedSlot}
                                        isSubmitting={isSubmitting}
                                        onSubmitClicked={onSubmitClicked}
                                    />{" "}
                                </div>
                            )}
                        </div>
                    }
                </div>
            )}
            <div className="py-3">
                <p>
                    Zurück zum <a href="https://moimio.de">Moimio</a>
                </p>
            </div>
        </div>
    );
}

export default CustomerBooking;
